<script setup lang="ts">
import { ref, watch } from 'vue'
import { Head, router, useForm } from '@inertiajs/vue3'
import { notify } from '@kyvg/vue3-notification'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import Modal from '@/Components/Utils/Modal.vue'
import Tag from '@/Components/Utils/Tag.vue'
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import { InputText, ToggleSwitch } from 'primevue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faExternalLink, faGripVertical } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faExternalLink, faGripVertical)

interface Courier {
    id: number
    name: string
    tracking_url: string | null
    requires_tracking_number: boolean
    is_active: boolean
    sort_order: number
    shipments_count: number
}

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    couriers: Courier[]
}>()

const orderedCouriers = ref<Courier[]>([...props.couriers])
watch(() => props.couriers, couriers => orderedCouriers.value = [...couriers])

const draggingIndex = ref<number | null>(null)
const orderBeforeDrag = ref<number[]>([])
const isSavingOrder = ref(false)

const onDragStart = (index: number, event: DragEvent) => {
    draggingIndex.value = index
    orderBeforeDrag.value = orderedCouriers.value.map(courier => courier.id)
    event.dataTransfer?.setData('text/plain', String(index))
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'
}

const onDragEnter = (index: number) => {
    if (draggingIndex.value === null || draggingIndex.value === index) return

    const [moved] = orderedCouriers.value.splice(draggingIndex.value, 1)
    orderedCouriers.value.splice(index, 0, moved)
    draggingIndex.value = index
}

const onDragEnd = () => {
    draggingIndex.value = null

    const courierIds = orderedCouriers.value.map(courier => courier.id)
    if (courierIds.join() === orderBeforeDrag.value.join()) return

    isSavingOrder.value = true
    router.patch(route('kasir.settings.couriers.reorder'), { ids: courierIds }, {
        preserveScroll: true,
        preserveState: true,
        onError: () => {
            orderedCouriers.value = [...props.couriers]
            notify({ title: 'Gagal', text: 'Urutan ekspedisi gagal disimpan.', type: 'error' })
        },
        onFinish: () => isSavingOrder.value = false,
    })
}

const isModalOpen = ref(false)
const editingCourier = ref<Courier | null>(null)

const form = useForm({
    name: '',
    tracking_url: '',
    requires_tracking_number: true,
    is_active: true,
})

const openCreateModal = () => {
    editingCourier.value = null
    form.reset()
    form.clearErrors()
    isModalOpen.value = true
}

const openEditModal = (courier: Courier) => {
    editingCourier.value = courier
    form.clearErrors()
    form.name = courier.name
    form.tracking_url = courier.tracking_url ?? ''
    form.requires_tracking_number = courier.requires_tracking_number
    form.is_active = courier.is_active
    isModalOpen.value = true
}

const submit = () => {
    const options = {
        preserveScroll: true,
        onSuccess: () => {
            isModalOpen.value = false
            notify({
                title: 'Tersimpan',
                text: editingCourier.value ? 'Ekspedisi berhasil diperbarui.' : 'Ekspedisi berhasil ditambahkan.',
                type: 'success',
            })
        },
    }

    if (editingCourier.value) {
        form.patch(route('kasir.settings.couriers.update', editingCourier.value.id), options)
    } else {
        form.post(route('kasir.settings.couriers.store'), options)
    }
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #other>
            <Button type="create" label="Tambah ekspedisi" @click="openCreateModal" />
        </template>
    </PageHeading>

    <div class="px-4 py-6">
        <section class="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <table v-if="orderedCouriers.length" class="w-full text-sm">
                <thead class="bg-gray-50 text-xs uppercase text-gray-400">
                    <tr>
                        <th class="w-10 px-3 py-3">
                            <LoadingIcon v-if="isSavingOrder" />
                        </th>
                        <th class="px-3 py-3 text-left">Nama ekspedisi</th>
                        <th class="px-3 py-3 text-left">Link tracking</th>
                        <th class="px-3 py-3 text-left">No. resi</th>
                        <th class="px-3 py-3 text-left">Status</th>
                        <th class="px-3 py-3 text-right">Dipakai</th>
                        <th class="px-3 py-3" />
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                    <tr
                        v-for="(courier, index) in orderedCouriers"
                        :key="courier.id"
                        class="transition-colors"
                        :class="[
                            courier.is_active ? '' : 'opacity-50',
                            draggingIndex === index ? 'bg-cyan-50' : '',
                        ]"
                        @dragenter.prevent="onDragEnter(index)"
                        @dragover.prevent
                    >
                        <td
                            class="cursor-grab px-3 py-3 text-center text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                            draggable="true"
                            v-tooltip="'Geser untuk mengubah urutan'"
                            @dragstart="event => onDragStart(index, event)"
                            @dragend="onDragEnd"
                        >
                            <FontAwesomeIcon icon="fal fa-grip-vertical" fixed-width aria-hidden="true" />
                        </td>
                        <td class="px-3 py-3 font-medium">{{ courier.name }}</td>
                        <td class="max-w-xs px-3 py-3">
                            <a
                                v-if="courier.tracking_url"
                                :href="courier.tracking_url"
                                target="_blank"
                                rel="noopener"
                                class="inline-flex max-w-full items-center gap-x-1 text-blue-500 underline"
                            >
                                <span class="truncate">{{ courier.tracking_url }}</span>
                                <FontAwesomeIcon icon="fal fa-external-link" fixed-width aria-hidden="true" class="shrink-0" />
                            </a>
                            <span v-else class="italic text-gray-400">-</span>
                        </td>
                        <td class="px-3 py-3 text-gray-500">{{ courier.requires_tracking_number ? 'Wajib' : 'Opsional' }}</td>
                        <td class="px-3 py-3">
                            <Tag :label="courier.is_active ? 'Aktif' : 'Nonaktif'" :theme="courier.is_active ? 3 : 8" noHoverColor />
                        </td>
                        <td class="px-3 py-3 text-right tabular-nums text-gray-500">{{ courier.shipments_count }} pengiriman</td>
                        <td class="px-3 py-3">
                            <div class="flex justify-end gap-x-2">
                                <Button size="xs" type="edit" icon="far fa-pencil" @click="openEditModal(courier)" />
                                <ModalConfirmationDelete
                                    v-if="!courier.shipments_count"
                                    :title="`Hapus ekspedisi ${courier.name}?`"
                                    description="Ekspedisi ini belum pernah dipakai, jadi aman dihapus."
                                    :routeDelete="{ name: 'kasir.settings.couriers.delete', parameters: [courier.id], method: 'delete' }"
                                    preserveScroll
                                >
                                    <template #default="{ changeModel }">
                                        <Button size="xs" type="delete" icon="far fa-trash-alt" @click="() => changeModel()" />
                                    </template>
                                </ModalConfirmationDelete>
                                <Button
                                    v-else
                                    size="xs"
                                    type="delete"
                                    icon="far fa-trash-alt"
                                    disabled
                                    v-tooltip="'Sudah dipakai di data pengiriman. Nonaktifkan lewat tombol edit.'"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-else class="px-6 py-10 text-center text-sm text-gray-400">Belum ada ekspedisi.</div>
        </section>
        <p class="mt-3 text-xs text-gray-400">
            Geser ikon <FontAwesomeIcon icon="fal fa-grip-vertical" fixed-width aria-hidden="true" /> untuk mengubah urutan pilihan ekspedisi.
            Hanya ekspedisi aktif yang muncul di pilihan pengiriman pesanan. Data pengiriman lama tetap menyimpan nama ekspedisi saat dicatat.
        </p>
    </div>

    <Modal :isOpen="isModalOpen" width="w-full max-w-lg" @onClose="isModalOpen = false">
        <h3 class="mb-4 text-lg font-semibold text-gray-700">
            {{ editingCourier ? 'Ubah ekspedisi' : 'Tambah ekspedisi' }}
        </h3>

        <div class="flex flex-col gap-4">
            <div>
                <label class="mb-1 block text-sm text-gray-600">Nama ekspedisi <span class="text-red-500">*</span></label>
                <InputText
                    v-model="form.name"
                    placeholder="Contoh: SiCepat"
                    fluid
                    :class="form.errors.name ? 'errorShake' : ''"
                    :invalid="!!form.errors.name"
                    @input="form.clearErrors('name')"
                    @keydown.enter.prevent="submit"
                />
                <div v-if="form.errors.name" class="mt-1 text-xs text-red-500">{{ form.errors.name }}</div>
            </div>

            <div>
                <label class="mb-1 block text-sm text-gray-600">Link tracking</label>
                <InputText
                    v-model="form.tracking_url"
                    placeholder="https://www.jet.co.id/track"
                    fluid
                    :class="form.errors.tracking_url ? 'errorShake' : ''"
                    :invalid="!!form.errors.tracking_url"
                    @input="form.clearErrors('tracking_url')"
                    @keydown.enter.prevent="submit"
                />
                <div v-if="form.errors.tracking_url" class="mt-1 text-xs text-red-500">{{ form.errors.tracking_url }}</div>
                <div v-else class="mt-1 text-xs text-gray-400">Halaman cek resi ekspedisi. Muncul sebagai link "Lacak" di data pengiriman pesanan.</div>
            </div>

            <label class="flex items-center justify-between gap-x-4 text-sm text-gray-600">
                <span>
                    No. resi wajib diisi
                    <span class="block text-xs text-gray-400">Matikan untuk pengiriman tanpa nomor resi, misalnya kurir sendiri.</span>
                </span>
                <ToggleSwitch v-model="form.requires_tracking_number" />
            </label>

            <label class="flex items-center justify-between gap-x-4 text-sm text-gray-600">
                <span>
                    Aktif
                    <span class="block text-xs text-gray-400">Ekspedisi nonaktif tidak muncul di pilihan pengiriman.</span>
                </span>
                <ToggleSwitch v-model="form.is_active" />
            </label>
        </div>

        <div class="mt-6 flex justify-end gap-x-2">
            <Button type="tertiary" label="Batal" @click="isModalOpen = false" />
            <Button type="save" :loading="form.processing" label="Simpan" @click="submit" />
        </div>
    </Modal>
</template>
